import type { MouseEvent, JSX } from 'react'

import { ExternalLinkIcon, IconButton, LinkIcon } from '@buffer-mono/popcorn'

import { useCommentData } from '../Comments/context/CommentContext'
import { copyCommentLinkToClipboard } from './copyCommentLink'

import styles from './CommentHoverActions.module.css'

/** The comment row is a click-to-expand target; these actions handle themselves. */
const stopExpansion = (event: MouseEvent): void => {
  event.stopPropagation()
}

/**
 * Comment actions in the top-right of the header, revealed on hover/focus:
 * open the comment on the source network, and copy its in-app link. Reads the
 * comment id from context, so the same component serves the main comment, each
 * ancestor, and each reply. Shown on collapsed and expanded cards alike; the
 * hover reveal lives in the card's CSS.
 */
export function CommentHoverActions(): JSX.Element {
  const comment = useCommentData()

  // The comment's link on the source network: its own permalink when the
  // network exposes one, otherwise the parent post's. Absent for some
  // networks/comments — the action hides rather than opening a dead tab.
  const externalUrl = comment.publicUrl ?? comment.post?.externalLink ?? null

  return (
    <CommentHoverActionsView
      externalUrl={externalUrl}
      onCopyLink={(): void => {
        void copyCommentLinkToClipboard(comment.id)
      }}
    />
  )
}

export function CommentHoverActionsView({
  externalUrl,
  onCopyLink,
}: {
  externalUrl: string | null
  onCopyLink: () => void
}): JSX.Element {
  return (
    <div
      className={styles.actions}
      data-comment-actions
      data-engagement-hover-actions
    >
      {externalUrl && (
        <IconButton
          as="a"
          href={externalUrl}
          target="_blank"
          rel="noopener noreferrer"
          label="Open in new tab"
          tooltip="Open in new tab"
          variant="tertiary"
          size="small"
          onClick={stopExpansion}
        >
          <ExternalLinkIcon stroke={2.5} />
        </IconButton>
      )}
      <IconButton
        type="button"
        label="Copy link"
        tooltip="Copy link"
        variant="tertiary"
        size="small"
        onClick={(event): void => {
          stopExpansion(event)
          onCopyLink()
        }}
      >
        <LinkIcon stroke={2.5} />
      </IconButton>
    </div>
  )
}
